import React from 'react';
import { Tag } from 'antd';
import { colors, typography } from './tokens';

export type StatusPillVariant = 'positive' | 'neutral' | 'negative' | 'warning';

export interface StatusPillProps {
  variant?: StatusPillVariant;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  icon?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  role?: string;
  'aria-label'?: string;
}

const variantStyles: Record<StatusPillVariant, {
  backgroundColor: string;
  color: string;
  borderColor: string;
}> = {
  positive: {
    backgroundColor: colors.status.positive,
    color: colors.text.positive.onPositive,
    borderColor: colors.status.positive,
  },
  neutral: {
    backgroundColor: colors.status.neutral,
    color: colors.text.neutral.onNeutral,
    borderColor: colors.status.neutral,
  },
  negative: {
    backgroundColor: colors.status.negative,
    color: colors.text.danger.onDanger,
    borderColor: colors.status.negative,
  },
  warning: {
    backgroundColor: colors.status.warning,
    color: colors.text.warning.onWarning,
    borderColor: colors.status.warning,
  },
};

export const StatusPill: React.FC<StatusPillProps> = ({ 
  variant = 'positive', 
  children,
  className = '',
  style,
  icon,
  size = 'medium',
  role = 'status',
  'aria-label': ariaLabel,
  ...restProps
}) => {
  const styles = variantStyles[variant];
  
  const sizeStyles = {
    small: { padding: '2px 8px', fontSize: '11px' },
    medium: { padding: '4px 12px', fontSize: typography.fontSize.xs },
    large: { padding: '6px 16px', fontSize: '14px' },
  };
  
  return (
    <Tag
      className={`atlas-status-pill atlas-status-pill-${variant} atlas-status-pill-${size} ${className}`}
      role={role}
      aria-label={ariaLabel}
      style={{
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        borderColor: styles.borderColor,
        border: 'none',
        borderRadius: '9999px',
        ...sizeStyles[size],
        fontWeight: typography.fontWeight.medium,
        fontFamily: typography.fontFamily.primary,
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: icon ? '4px' : '0',
        margin: 0,
        ...style,
      }}
      {...restProps}
    >
      {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
      {children}
    </Tag>
  );
};

// Common status mappings for convenience
export const getStatusVariant = (status: string): StatusPillVariant => {
  const normalizedStatus = status.toLowerCase();
  
  const statusMap: Record<string, StatusPillVariant> = {
    // Positive statuses
    active: 'positive',
    completed: 'positive',
    approved: 'positive',
    success: 'positive',
    paid: 'positive',
    
    // Neutral statuses
    pending: 'neutral',
    draft: 'neutral',
    processing: 'neutral',
    
    // Negative statuses
    cancelled: 'negative',
    failed: 'negative',
    rejected: 'negative',
    overdue: 'negative',
    attention: 'negative',
    
    // Warning statuses
    warning: 'warning',
    expiring: 'warning',
    review: 'warning',
  };
  
  return statusMap[normalizedStatus] || 'neutral';
};